import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Bookmark, BookmarkCheck, ExternalLink, ArrowLeft, Calendar, Globe2, Shield } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function AgencyDetail() {
  const { id } = useParams();
  const { user, refreshUser } = useAuth();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const saved = new Set(user?.saved_agencies || []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/agencies/${id}`);
        setAgency(data);
      } catch {
        toast.error("Agency not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const toggleSave = async () => {
    try {
      const { data } = await api.post("/agencies/save", { agency_id: id });
      toast.success(data.action === "saved" ? "Added to your favorites" : "Removed from favorites");
      await refreshUser();
    } catch {
      toast.error("Action failed");
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#0A0C10] pt-32 text-center overline" data-testid="agency-loading">
        Loading…
      </div>
    );

  if (!agency)
    return (
      <div className="min-h-screen bg-[#0A0C10] pt-32 text-center text-slate-400">
        Not found
      </div>
    );

  return (
    <div className="min-h-screen bg-[#0A0C10] pt-28 pb-20">
      <div className="max-w-5xl mx-auto px-6">
        <Link to="/agencies" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8" data-testid="back-to-agencies">
          <ArrowLeft className="w-4 h-4" /> Back to directory
        </Link>

        <div className="glass-card p-10 lg:p-12 fade-up">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <div className="text-5xl mb-4">{agency.flag}</div>
              <div className="overline">{agency.country}</div>
              <h1 className="font-display text-4xl lg:text-6xl mt-3 tracking-tight">{agency.name}</h1>
              <div className="text-slate-400 mt-2 text-lg">{agency.full_name}</div>
            </div>
            <button
              onClick={toggleSave}
              className={`btn-ghost-gold inline-flex items-center gap-2 ${saved.has(agency.id) ? "bg-[#7DA3D9]/10 border-[#7DA3D9]" : ""}`}
              data-testid="save-agency-btn"
            >
              {saved.has(agency.id) ? (
                <>
                  <BookmarkCheck className="w-4 h-4" /> Saved
                </>
              ) : (
                <>
                  <Bookmark className="w-4 h-4" /> Save to Favorites
                </>
              )}
            </button>
          </div>

          <div className="divider-gold my-10" />

          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="overline flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> Founded</div>
              <div className="font-display text-3xl text-[#B8D0EE] mt-2">{agency.founded}</div>
            </div>
            <div>
              <div className="overline flex items-center gap-2"><Globe2 className="w-3.5 h-3.5" /> Territory</div>
              <div className="font-display text-2xl mt-2">{agency.country}</div>
            </div>
            <div>
              <div className="overline flex items-center gap-2"><Shield className="w-3.5 h-3.5" /> Rights handled</div>
              <div className="flex flex-wrap gap-2 mt-3">
                {(agency.rights || []).map((r) => (
                  <span key={r} className="px-3 py-1 rounded-full text-xs border border-[#7DA3D9]/30 bg-[#7DA3D9]/5 text-[#B8D0EE] uppercase tracking-wider">
                    {r}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="divider-gold my-10" />

          <div>
            <div className="overline">About</div>
            <p className="text-slate-200 text-lg mt-4 leading-relaxed">{agency.description}</p>
          </div>

          {(agency.address || agency.phone) && (
            <>
              <div className="divider-gold my-10" />
              <div className="grid md:grid-cols-2 gap-8">
                {agency.address && (
                  <div data-testid="agency-address">
                    <div className="overline">Address</div>
                    <p className="text-slate-200 mt-3 leading-relaxed">{agency.address}</p>
                  </div>
                )}
                {agency.phone && (
                  <div data-testid="agency-phone">
                    <div className="overline">Telephone</div>
                    <a href={`tel:${agency.phone.replace(/\s/g, "")}`} className="text-[#B8D0EE] hover:text-[#7DA3D9] mt-3 block font-display text-xl">
                      {agency.phone}
                    </a>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="mt-10">
            <a
              href={agency.website}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold inline-flex items-center gap-2"
              data-testid="agency-visit-btn"
            >
              Visit official website <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
